"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useId,
  useInsertionEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
  type RefCallback,
} from "react";
import { createPortal } from "react-dom";
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion, useIsPresent, type Transition, type Variants } from "motion/react";
import { Button } from "@/components/wingo/ui/button";
import {
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerRoot,
  DrawerTrigger,
  type DrawerContentProps,
} from "@/components/wingo/ui/drawer";
import { useControllableState } from "@/components/wingo/hooks/use-controllable-state";
import { useIsMobile } from "@/components/wingo/hooks/use-media-query";
import { isTypingTarget, matchesShortcut, useShortcut, useShortcutLabel } from "@/components/wingo/hooks/use-shortcut";
import { durations, easings, springs, useMotionPreset, useMotionTransition, usePrefersReducedMotion } from "@/components/wingo/lib/motion";
import { useDefaults } from "@/components/wingo/lib/ui-config";
import { cn } from "@/components/wingo/lib/utils";
import { inertProps, withRef } from "@/components/wingo/lib/with-ref";

export type DropdownMenuSize = "sm" | "md";
export type DropdownMenuRadius = "none" | "sm" | "md" | "lg" | "full";
export type DropdownMenuSide = "top" | "right" | "bottom" | "left";
export type DropdownMenuAlign = "start" | "center" | "end";
export type DropdownMenuSlot = "content" | "item" | "icon" | "label" | "description" | "shortcut" | "separator" | "sheet";
export type MenuTone = "neutral" | "danger";

// ---------------------------------------------------------------- entries (shared with context-menu and button-group)

export type MenuItemEntry = {
  type?: "item";
  id?: string;
  label: ReactNode;
  icon?: ReactNode;
  description?: ReactNode;
  // mod+k notation; shown at the end of the row and run while the menu is open
  shortcut?: string;
  // a count or a short tag before the shortcut; strings and numbers get the pill look
  badge?: ReactNode;
  onSelect?: (event: Event) => void;
  // renders an <a>; for router links use the parts with asChild
  href?: string;
  target?: string;
  disabled?: boolean;
  tone?: MenuTone;
  // selecting it does not close the menu
  keepOpen?: boolean;
};

export type MenuCheckboxEntry = {
  type: "checkbox";
  id?: string;
  label: ReactNode;
  icon?: ReactNode;
  description?: ReactNode;
  shortcut?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  // default true: toggling keeps the menu open
  keepOpen?: boolean;
};

export type MenuRadioOption = {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
};

export type MenuRadioEntry = {
  type: "radio";
  id?: string;
  // a group label above the options
  label?: ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  options: MenuRadioOption[];
  // default true: picking an option keeps the menu open
  keepOpen?: boolean;
};

export type MenuSubmenuEntry = {
  type: "submenu";
  id?: string;
  label: ReactNode;
  icon?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  items: MenuEntry[];
};

export type MenuGroupEntry = { type: "group"; id?: string; label?: ReactNode; items: MenuEntry[] };
export type MenuLabelEntry = { type: "label"; id?: string; label: ReactNode };
export type MenuSeparatorEntry = { type: "separator"; id?: string };

export type MenuEntry =
  | MenuItemEntry
  | MenuCheckboxEntry
  | MenuRadioEntry
  | MenuSubmenuEntry
  | MenuGroupEntry
  | MenuLabelEntry
  | MenuSeparatorEntry;

export type DropdownMenuEntry = MenuEntry;

// ---------------------------------------------------------------- classes (shared with context-menu and button-group)

export const MENU_CLASSES = {
  // add the primitive's own max height: max-h-(--radix-dropdown-menu-content-available-height)
  content:
    "z-popover min-w-48 max-w-[min(calc(100vw-24px),384px)] overflow-y-auto overscroll-contain rounded-2xl bg-popover p-1.5 text-popover-foreground shadow-floating outline-hidden",
  item: "relative flex h-9 w-full cursor-pointer touch-manipulation select-none items-center gap-2.5 rounded-[10px] px-2.5 text-sm outline-hidden [-webkit-tap-highlight-color:transparent] transition-colors duration-fast ease-smooth pointer-coarse:h-11 data-[highlighted]:bg-foreground/5 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  itemWithDescription: "h-auto min-h-9 items-start py-2 pointer-coarse:h-auto pointer-coarse:min-h-11",
  itemDanger: "text-destructive-soft-foreground data-[highlighted]:bg-destructive-soft",
  subTrigger: "data-[state=open]:bg-foreground/5",
  icon: "flex shrink-0 items-center justify-center text-muted-foreground",
  text: "flex min-w-0 flex-1 flex-col",
  label: "min-w-0 truncate",
  description: "text-xs text-muted-foreground",
  shortcut: "ms-auto shrink-0 ps-4 text-xs tracking-[0.08em] text-muted-foreground pointer-coarse:hidden",
  badge:
    "inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-foreground/10 px-1.5 text-[11px] font-semibold tabular-nums text-foreground",
  groupLabel: "px-2.5 pt-2 pb-1 text-xs font-medium text-muted-foreground",
  separator: "-mx-1.5 my-1.5 h-px bg-foreground/10",
  subChevron: "ms-auto size-4 text-muted-foreground",
  // the sm checkbox look; the sheet takes the md box (18px)
  check:
    "flex size-4 shrink-0 items-center justify-center rounded-[4px] border-[1.5px] border-foreground/30 text-background transition-[background-color,border-color] duration-fast ease-smooth data-[state=checked]:border-transparent data-[state=checked]:bg-foreground data-[state=indeterminate]:border-transparent data-[state=indeterminate]:bg-foreground",
  // the trailing check of a selected radio item
  radioCheck: "ms-auto size-4 shrink-0",
  sheetItem:
    "relative flex h-12 w-full cursor-pointer touch-manipulation select-none items-center gap-3 rounded-[12px] px-3 text-start text-[15px] outline-hidden [-webkit-tap-highlight-color:transparent] transition-colors duration-fast ease-smooth hover:bg-foreground/5 focus-visible:bg-foreground/5 active:bg-foreground/5 active:duration-0 disabled:pointer-events-none disabled:opacity-50 data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  sheetItemWithDescription: "h-auto min-h-14 py-2",
  sheetItemDanger:
    "text-destructive-soft-foreground hover:bg-destructive-soft focus-visible:bg-destructive-soft active:bg-destructive-soft",
  sheetGroupLabel: "px-3 pt-3 pb-1 text-[13px] font-medium text-muted-foreground",
  sheetSeparator: "mx-3 my-2 h-px bg-foreground/10",
  sheetBadge: "h-6 min-w-6 px-2 text-xs",
} as const;

// the item sizes: 32 / 36px, 44px on a coarse pointer either way
export const MENU_ITEM_SIZES: Record<DropdownMenuSize, string> = {
  sm: "h-8 gap-2 px-2 text-[13px] [&_svg:not([class*='size-'])]:size-3.5",
  md: "h-9 gap-2.5 px-2.5 text-sm [&_svg:not([class*='size-'])]:size-4",
};

const DESCRIPTION_SIZES: Record<DropdownMenuSize, string> = {
  sm: "h-auto min-h-8 items-start py-1.5 pointer-coarse:h-auto pointer-coarse:min-h-11",
  md: MENU_CLASSES.itemWithDescription,
};

// the surface radii, and the items inside at the surface radius minus the 6px padding
export const MENU_RADII: Record<DropdownMenuRadius, { content: string; item: string }> = {
  none: { content: "rounded-none", item: "rounded-none" },
  sm: { content: "rounded-[12px]", item: "rounded-[6px]" },
  md: { content: "rounded-2xl", item: "rounded-[10px]" },
  lg: { content: "rounded-3xl", item: "rounded-[18px]" },
  full: { content: "rounded-3xl", item: "rounded-[18px]" },
};

// a desktop item row for a size, radius and tone; context-menu builds its rows with this too
export function menuItemClass({
  size = "md",
  radius = "md",
  tone = "neutral",
  description = false,
}: { size?: DropdownMenuSize; radius?: DropdownMenuRadius; tone?: MenuTone; description?: boolean } = {}) {
  const safeSize: DropdownMenuSize = Object.hasOwn(MENU_ITEM_SIZES, size) ? size : "md";
  return cn(
    MENU_CLASSES.item,
    MENU_ITEM_SIZES[safeSize],
    (MENU_RADII[radius] ?? MENU_RADII.md).item,
    description && DESCRIPTION_SIZES[safeSize],
    tone === "danger" && MENU_CLASSES.itemDanger,
  );
}

// a sheet row; rows are 48px, 56px with a description
export function menuSheetItemClass({ tone = "neutral", description = false }: { tone?: MenuTone; description?: boolean } = {}) {
  return cn(
    MENU_CLASSES.sheetItem,
    description && MENU_CLASSES.sheetItemWithDescription,
    tone === "danger" && MENU_CLASSES.sheetItemDanger,
  );
}

const SIZES: DropdownMenuSize[] = ["sm", "md"];
const RADII: DropdownMenuRadius[] = ["none", "sm", "md", "lg", "full"];

const filled = (node: ReactNode) => node !== undefined && node !== null && node !== false && node !== "";

// ---------------------------------------------------------------- item inner layout

export type MenuItemContentProps = {
  label: ReactNode;
  icon?: ReactNode;
  description?: ReactNode;
  badge?: ReactNode;
  shortcut?: string;
  // a trailing chevron (submenu triggers)
  submenu?: boolean;
  // a leading checkbox box or a trailing check
  indicator?: "checkbox" | "radio";
  checked?: boolean | "indeterminate";
  size?: DropdownMenuSize;
  // the larger sheet row type
  sheet?: boolean;
  tone?: MenuTone;
  // data-slot prefix, e.g. "context-menu"
  slot?: string;
  classNames?: Partial<Record<"icon" | "label" | "description" | "shortcut" | "badge" | "indicator", string>>;
};

function CheckboxMark({
  checked,
  sheet,
  slot,
  className,
}: {
  checked: boolean | "indeterminate";
  sheet: boolean;
  slot: string;
  className?: string;
}) {
  const transition = useMotionTransition(springs.snappy);
  const state = checked === "indeterminate" ? "indeterminate" : checked ? "checked" : "unchecked";
  const on = state !== "unchecked";
  return (
    <span
      data-slot={`${slot}-item-indicator`}
      data-state={state}
      aria-hidden
      className={cn(MENU_CLASSES.check, sheet && "size-[18px] rounded-[5px]", className)}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={3.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={sheet ? "size-3.5" : "size-3"}
      >
        <motion.path
          d={state === "indeterminate" ? "M6 12h12" : "M5 12.5l4.5 4.5L19 7.5"}
          initial={false}
          animate={{ pathLength: on ? 1 : 0, opacity: on ? 1 : 0 }}
          transition={transition}
        />
      </svg>
    </span>
  );
}

function RadioMark({ checked, slot, className }: { checked: boolean; slot: string; className?: string }) {
  const transition = useMotionTransition(springs.snappy);
  return (
    <svg
      data-slot={`${slot}-item-indicator`}
      data-state={checked ? "checked" : "unchecked"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.25}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className={cn(MENU_CLASSES.radioCheck, "self-center", className)}
    >
      <motion.path
        d="M4.5 12.5l5 5L19.5 7"
        initial={false}
        animate={{ pathLength: checked ? 1 : 0, opacity: checked ? 1 : 0 }}
        transition={transition}
      />
    </svg>
  );
}

// the inside of a row: checkbox, icon, label and description, badge, shortcut, chevron or check. drop it into any
// radix menu item (dropdown, context menu) that carries the item classes
function MenuItemContentRender({
  label,
  icon,
  description,
  badge,
  shortcut,
  submenu = false,
  indicator,
  checked = false,
  size = "md",
  sheet = false,
  tone = "neutral",
  slot = "dropdown-menu",
  classNames,
}: MenuItemContentProps) {
  const { text } = useShortcutLabel(shortcut?.trim() || null);
  const hasDescription = filled(description);
  const hasIcon = filled(icon);
  // desktop rows with a description align to the first line; sheet rows stay centered like ios
  const top = hasDescription && !sheet;
  const offset = top && (size === "sm" ? "mt-[3px]" : "mt-0.5");
  // the checkbox takes the icon's place; next to an icon it moves to the end, so labels stay in one column
  const checkbox =
    indicator === "checkbox" ? (
      <CheckboxMark
        checked={checked}
        sheet={sheet}
        slot={slot}
        className={cn(hasIcon ? "ms-auto self-center" : offset, classNames?.indicator)}
      />
    ) : null;

  return (
    <>
      {!hasIcon && checkbox}
      {hasIcon && (
        <span
          data-slot={`${slot}-item-icon`}
          aria-hidden
          className={cn(
            MENU_CLASSES.icon,
            sheet ? "min-w-[18px]" : size === "sm" ? "min-w-3.5" : "min-w-4",
            tone === "danger" && "text-current",
            offset,
            classNames?.icon,
          )}
        >
          {icon}
        </span>
      )}
      <span data-slot={`${slot}-item-text`} className={MENU_CLASSES.text}>
        <span data-slot={`${slot}-item-label`} className={cn(MENU_CLASSES.label, sheet ? "leading-[22px]" : "leading-5", classNames?.label)}>
          {label}
        </span>
        {hasDescription && (
          <span
            data-slot={`${slot}-item-description`}
            className={cn(
              MENU_CLASSES.description,
              sheet ? "text-[13px] leading-[18px]" : "mt-0.5 leading-4",
              tone === "danger" && "text-current opacity-80",
              classNames?.description,
            )}
          >
            {description}
          </span>
        )}
      </span>
      {filled(badge) &&
        (isValidElement(badge) ? (
          <span data-slot={`${slot}-item-badge`} className={cn("flex shrink-0 self-center", classNames?.badge)}>
            {badge}
          </span>
        ) : (
          <span
            data-slot={`${slot}-item-badge`}
            className={cn(MENU_CLASSES.badge, "self-center", sheet && MENU_CLASSES.sheetBadge, classNames?.badge)}
          >
            {badge}
          </span>
        ))}
      {text && (
        <span data-slot={`${slot}-item-shortcut`} aria-hidden className={cn(MENU_CLASSES.shortcut, "self-center", classNames?.shortcut)}>
          {text}
        </span>
      )}
      {hasIcon && checkbox}
      {submenu && <ChevronRight aria-hidden className={cn(MENU_CLASSES.subChevron, "self-center")} />}
      {indicator === "radio" && <RadioMark checked={checked === true} slot={slot} className={classNames?.indicator} />}
    </>
  );
}

// ---------------------------------------------------------------- contexts

type RootContextValue = {
  // true: every part renders as the action sheet
  sheet: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  container: HTMLElement | null | undefined;
  shortcut: string | undefined;
  // the trigger was pressed with a key: the panel focuses its first item once it mounts
  keyboardOpenRef: { current: boolean };
};

const RootContext = createContext<RootContextValue | null>(null);

function useRoot(part: string) {
  const context = useContext(RootContext);
  if (!context) throw new Error(`<${part}> must be used inside <DropdownMenuRoot> or <DropdownMenu>`);
  return context;
}

// true when the keyboard event ran a shortcut
type ShortcutHandler = (event: ReactKeyboardEvent<HTMLElement>) => boolean;

type ContentContextValue = {
  size: DropdownMenuSize;
  radius: DropdownMenuRadius;
  minWidth: number;
  loop: boolean;
  classNames: Partial<Record<DropdownMenuSlot, string>> | undefined;
  register: ((handler: ShortcutHandler) => () => void) | undefined;
};

const DEFAULT_CONTENT: ContentContextValue = {
  size: "md",
  radius: "md",
  minWidth: 192,
  loop: true,
  classNames: undefined,
  register: undefined,
};

const ContentContext = createContext<ContentContextValue>(DEFAULT_CONTENT);

type SubContextValue = { id: string; open: boolean };
const SubContext = createContext<SubContextValue | null>(null);

type RadioContextValue = { value: string; setValue: (value: string) => void };
const RadioContext = createContext<RadioContextValue | null>(null);

type SheetPageEntry = { id: string; title: ReactNode; from: HTMLElement | null };
type SheetEngineValue = {
  push: (page: SheetPageEntry) => void;
  pop: () => void;
  // the element each pushed page renders its submenu into
  containers: Record<string, HTMLElement>;
};
const SheetEngineContext = createContext<SheetEngineValue | null>(null);

// ---------------------------------------------------------------- shortcuts and selection

// the item shortcuts of one open menu: items register while mounted, the content's keydown runs the first match
function useShortcutRegistry() {
  const handlers = useRef(new Set<ShortcutHandler>());
  const register = useCallback((handler: ShortcutHandler) => {
    handlers.current.add(handler);
    return () => {
      handlers.current.delete(handler);
    };
  }, []);
  const onKeyDown = useCallback((event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.defaultPrevented || event.repeat || event.nativeEvent.isComposing) return;
    // a bare key never fires while typing in a field inside the menu
    if (!event.ctrlKey && !event.metaKey && !event.altKey && isTypingTarget(event.target)) return;
    for (const handler of Array.from(handlers.current)) {
      if (handler(event)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
    }
  }, []);
  return { register, onKeyDown };
}

function useLatest<T>(value: T) {
  const ref = useRef(value);
  useInsertionEffect(() => {
    ref.current = value;
  });
  return ref;
}

function useItemShortcut(shortcut: string | undefined, disabled: boolean, run: () => void) {
  const { register } = useContext(ContentContext);
  const latest = useLatest({ shortcut: shortcut?.trim(), disabled, run });
  const active = Boolean(shortcut?.trim());
  useEffect(() => {
    if (!register || !active) return;
    return register((event) => {
      const { shortcut: combo, disabled: off, run: fire } = latest.current;
      if (!combo || off || !matchesShortcut(event, combo)) return false;
      fire();
      return true;
    });
  }, [register, active, latest]);
}

type SelectHandler = ((event: Event) => void) | undefined;

// runs a selection the way the current mode expects. desktop: onSelect first, then close unless it called
// preventDefault (radix's order). sheet: close first and run onSelect a frame later, so a navigation or a dialog
// never fights the sheet's exit
function useSelectRunner() {
  const { sheet, setOpen } = useRoot("DropdownMenuItem");
  return useCallback(
    (onSelect: SelectHandler, keepOpen: boolean) => {
      const event = new CustomEvent("menu.itemSelect", { bubbles: true, cancelable: true });
      if (keepOpen) {
        onSelect?.(event);
        return;
      }
      if (sheet) {
        setOpen(false);
        if (onSelect) requestAnimationFrame(() => onSelect(event));
        return;
      }
      onSelect?.(event);
      if (!event.defaultPrevented) setOpen(false);
    },
    [sheet, setOpen],
  );
}

type Actionable = MenuItemEntry | MenuCheckboxEntry;

// the enabled item or checkbox entry this key combo belongs to, looking inside groups and submenus
function findShortcut(entries: MenuEntry[], event: ReactKeyboardEvent<HTMLElement>): Actionable | null {
  for (const entry of entries) {
    if (entry.type === "group") {
      const hit = findShortcut(entry.items, event);
      if (hit) return hit;
    } else if (entry.type === "submenu") {
      if (entry.disabled) continue;
      const hit = findShortcut(entry.items, event);
      if (hit) return hit;
    } else if (entry.type === undefined || entry.type === "item" || entry.type === "checkbox") {
      const combo = entry.shortcut?.trim();
      if (!entry.disabled && combo && matchesShortcut(event, combo)) return entry;
    }
  }
  return null;
}

// submenu entries are not mounted until opened; their shortcuts still work from the parent menu
function useEntryShortcuts(items: MenuEntry[], disabled: boolean) {
  const { register } = useContext(ContentContext);
  const run = useSelectRunner();
  const latest = useLatest({ items, run, disabled });
  useEffect(() => {
    if (!register) return;
    return register((event) => {
      const { items: entries, run: fire, disabled: off } = latest.current;
      if (off) return false;
      const hit = findShortcut(entries, event);
      if (!hit) return false;
      if (hit.type === "checkbox") {
        hit.onCheckedChange(!hit.checked);
        fire(undefined, hit.keepOpen ?? true);
      } else {
        fire(hit.onSelect, hit.keepOpen ?? false);
      }
      return true;
    });
  }, [register, latest]);
}

// true when there is at least one enabled thing to pick
function hasActionable(entries: MenuEntry[] | undefined): boolean {
  if (!entries) return false;
  return entries.some((entry) => {
    switch (entry.type) {
      case "separator":
      case "label":
        return false;
      case "group":
        return hasActionable(entry.items);
      case "submenu":
        return !entry.disabled && hasActionable(entry.items);
      case "radio":
        return entry.options.some((option) => !option.disabled);
      default:
        return !entry.disabled;
    }
  });
}

// ---------------------------------------------------------------- root and trigger

export type DropdownMenuRootProps = {
  // controlled open state; pair it with onOpenChange
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  // radix: blocks pointer events outside while open (desktop)
  modal?: boolean;
  // an action sheet below 768px
  responsive?: boolean;
  // a key combo in mod+k notation that opens and closes the menu from anywhere on the page
  shortcut?: string;
  // portal target (default document.body)
  container?: HTMLElement | null;
  dir?: "ltr" | "rtl";
  children?: ReactNode;
};

function DropdownMenuRootRender(dropdownMenuRootProps: DropdownMenuRootProps) {
  const {
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    modal = true,
    responsive = true,
    shortcut,
    container,
    dir,
    children,
  } = useDefaults("dropdown-menu", dropdownMenuRootProps);
  const [open, setOpen] = useControllableState({ value: openProp, defaultValue: defaultOpen, onChange: onOpenChange });
  const isMobile = useIsMobile();
  const sheet = responsive && isMobile;
  const combo = shortcut?.trim() || undefined;

  const keyboardOpenRef = useRef(false);

  useShortcut(combo, () => setOpen((current) => !current));

  const value = useMemo<RootContextValue>(
    () => ({ sheet, open, setOpen, container, shortcut: combo, keyboardOpenRef }),
    [sheet, open, setOpen, container, combo],
  );

  return (
    <RootContext.Provider value={value}>
      {sheet ? (
        <DrawerRoot open={open} onOpenChange={setOpen} container={container}>
          {children}
        </DrawerRoot>
      ) : (
        <DropdownMenuPrimitive.Root open={open} onOpenChange={setOpen} modal={modal} dir={dir}>
          {children}
        </DropdownMenuPrimitive.Root>
      )}
    </RootContext.Provider>
  );
}

export type DropdownMenuTriggerProps = ComponentProps<typeof DropdownMenuPrimitive.Trigger>;

const OPEN_KEYS = new Set(["Enter", " ", "ArrowDown"]);

function DropdownMenuTriggerRender({ onPointerDown, onKeyDown, ...props }: DropdownMenuTriggerProps) {
  const { sheet, shortcut, keyboardOpenRef } = useRoot("DropdownMenuTrigger");
  const { aria } = useShortcutLabel(shortcut);
  // the sheet is a dialog: its trigger is the drawer's, so focus returns to it on close
  if (sheet)
    return (
      <DrawerTrigger
        data-slot="dropdown-menu-trigger"
        aria-keyshortcuts={aria}
        onPointerDown={(event) => {
          onPointerDown?.(event);
          if (event.nativeEvent.isTrusted || event.pointerType) keyboardOpenRef.current = false;
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (OPEN_KEYS.has(event.key)) keyboardOpenRef.current = true;
        }}
        {...props}
      />
    );
  return (
    <DropdownMenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      aria-keyshortcuts={aria}
      onPointerDown={(event) => {
        onPointerDown?.(event);
        // motion's keyboard press (a Button's whileTap) answers Enter with a synthetic pointerdown; radix opens on
        // pointerdown and again on the Enter itself, so that fake press would close the menu it just opened
        if (!event.nativeEvent.isTrusted && !event.pointerType) event.preventDefault();
        else keyboardOpenRef.current = false;
      }}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        // the same fake press tells radix a pointer opened the menu, so it would not focus the first item
        if (OPEN_KEYS.has(event.key)) keyboardOpenRef.current = true;
      }}
      {...props}
    />
  );
}

// ---------------------------------------------------------------- sheet (phones)

const ROOT_PAGE = "root";
const ROW_SELECTOR = '[data-sheet-row]:not(:disabled):not([aria-disabled="true"])';
const NAV_KEYS = new Set(["ArrowDown", "ArrowUp", "Home", "End", "ArrowLeft", "ArrowRight"]);

// a pushed page slides in from the right over the page below, which drifts left and fades (ios navigation)
const PAGE_VARIANTS: Variants = {
  enter: { x: "100%", opacity: 1 },
  top: { x: 0, opacity: 1 },
  below: { x: "-30%", opacity: 0 },
  exit: { x: "100%", opacity: 1 },
};
const PAGE_VARIANTS_REDUCED: Variants = {
  enter: { opacity: 0 },
  top: { opacity: 1 },
  below: { opacity: 0 },
  exit: { opacity: 0 },
};
const PAGE_TRANSITION: Transition = { x: springs.smooth, opacity: { duration: durations.normal, ease: easings.out } };
const PAGE_TRANSITION_REDUCED: Transition = { duration: durations.fast, ease: "linear" };

const BACK_BUTTON =
  "flex h-11 min-w-0 cursor-pointer touch-manipulation select-none items-center gap-0.5 justify-self-start rounded-[12px] ps-1.5 pe-3 text-[15px] font-medium outline-hidden [-webkit-tap-highlight-color:transparent] transition-colors duration-fast ease-smooth hover:bg-foreground/5 focus-visible:bg-foreground/5 active:bg-foreground/5 active:duration-0";

// centered ios action sheet header: small title, quiet description
const SHEET_DRAWER_CLASSNAMES: DrawerContentProps["classNames"] = {
  header: "gap-0.5 pb-2",
  title: "text-[15px] leading-5",
  description: "text-[13px] leading-[18px]",
};

function SheetPage({
  id,
  root,
  top,
  title,
  label,
  backLabel,
  onBack,
  onHeight,
  onRegister,
  children,
}: {
  id: string;
  root: boolean;
  top: boolean;
  title: ReactNode;
  label: string;
  backLabel: string;
  onBack: () => void;
  onHeight: (height: number) => void;
  onRegister: (id: string, node: HTMLElement | null) => void;
  children?: ReactNode;
}) {
  const isPresent = useIsPresent();
  const reduced = usePrefersReducedMotion();
  const active = top && isPresent;
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // the sheet's height follows the page on top
  useEffect(() => {
    const el = ref.current;
    if (!el || !active) return;
    const observer = new ResizeObserver(() => onHeight(el.offsetHeight));
    observer.observe(el);
    return () => observer.disconnect();
  }, [active, onHeight]);

  // a pushed page takes focus on its first row, so arrows and Enter keep working
  useEffect(() => {
    if (root) return;
    const frame = requestAnimationFrame(() =>
      ref.current?.querySelector<HTMLElement>(`[role=menu] ${ROW_SELECTOR}`)?.focus({ preventScroll: true }),
    );
    return () => cancelAnimationFrame(frame);
  }, [root]);

  const register = useCallback<RefCallback<HTMLDivElement>>(
    // React 18: fără cleanup în ref callback; React cheamă ref-ul cu null la detașare
    (node) => onRegister(id, node),
    [id, onRegister],
  );

  return (
    <motion.div
      ref={ref}
      data-slot="dropdown-menu-sheet-page"
      data-active={active || undefined}
      {...inertProps(!active)}
      initial={root ? false : "enter"}
      animate={top ? "top" : "below"}
      exit="exit"
      variants={reduced ? PAGE_VARIANTS_REDUCED : PAGE_VARIANTS}
      transition={reduced ? PAGE_TRANSITION_REDUCED : PAGE_TRANSITION}
      className={cn("w-full bg-popover px-2", active ? "relative" : "absolute inset-x-0 top-0")}
    >
      {!root && (
        <div
          data-slot="dropdown-menu-sheet-back"
          className="sticky top-0 z-[1] grid h-12 grid-cols-[1fr_minmax(0,max-content)_1fr] items-center bg-popover"
        >
          <button type="button" data-sheet-row="" onClick={onBack} className={BACK_BUTTON}>
            <ChevronLeft aria-hidden className="size-5 shrink-0" />
            <span className="truncate">{backLabel}</span>
          </button>
          <span id={titleId} className="min-w-0 truncate px-2 text-center text-[15px] font-semibold">
            {title}
          </span>
        </div>
      )}
      <div
        ref={root ? undefined : register}
        role="menu"
        aria-orientation="vertical"
        aria-label={root ? label : undefined}
        aria-labelledby={root ? undefined : titleId}
        className="flex flex-col"
      >
        {children}
      </div>
    </motion.div>
  );
}

type SheetSurfaceProps = Pick<
  DrawerContentProps,
  "onOpenAutoFocus" | "onCloseAutoFocus" | "onEscapeKeyDown" | "onPointerDownOutside" | "onInteractOutside" | "onFocusOutside"
> & {
  title?: ReactNode;
  description?: ReactNode;
  sheetTitle: string;
  showCancel: boolean;
  cancelLabel: string;
  backLabel: string;
  loop: boolean;
  container: HTMLElement | null | undefined;
  classNames: Partial<Record<DropdownMenuSlot, string>> | undefined;
  className?: string;
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
  children?: ReactNode;
};

function SheetSurface({
  title,
  description,
  sheetTitle,
  showCancel,
  cancelLabel,
  backLabel,
  loop,
  container,
  classNames,
  className,
  onKeyDown,
  children,
  ...dismiss
}: SheetSurfaceProps) {
  const { open, keyboardOpenRef } = useRoot("DropdownMenuContent");
  const reduced = usePrefersReducedMotion();
  const [stack, setStack] = useState<SheetPageEntry[]>([]);
  const [containers, setContainers] = useState<Record<string, HTMLElement>>({});
  const [height, setHeight] = useState<number | "auto">("auto");
  const bodyRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);

  // every opening starts on the first page (the content stays mounted while closed)
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setStack([]);
      setHeight("auto");
    }
  }

  const push = useCallback((page: SheetPageEntry) => {
    setStack((current) => (current.some((p) => p.id === page.id) ? current : [...current, page]));
    bodyRef.current?.scrollTo({ top: 0 });
  }, []);

  const pop = useCallback(() => {
    const last = stack[stack.length - 1];
    if (!last) return;
    setStack(stack.slice(0, -1));
    // back to the row that opened the page, for keyboard users
    requestAnimationFrame(() => last.from?.focus({ preventScroll: true }));
  }, [stack]);

  const onRegister = useCallback((id: string, node: HTMLElement | null) => {
    setContainers((current) => {
      if (node) return current[id] === node ? current : { ...current, [id]: node };
      if (!(id in current)) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }, []);

  const onHeight = useCallback((next: number) => setHeight(next), []);
  const engine = useMemo<SheetEngineValue>(() => ({ push, pop, containers }), [push, pop, containers]);

  // arrows move between the rows of the page on top, left goes back, right opens a submenu row
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    onKeyDown(event);
    if (event.defaultPrevented || !NAV_KEYS.has(event.key) || isTypingTarget(event.target)) return;
    const page = viewportRef.current?.querySelector<HTMLElement>("[data-slot=dropdown-menu-sheet-page][data-active]");
    if (!page) return;
    const rows = Array.from(page.querySelectorAll<HTMLElement>(ROW_SELECTOR));
    const current = (event.target as HTMLElement).closest<HTMLElement>(ROW_SELECTOR);
    const index = current ? rows.indexOf(current) : -1;
    const last = rows.length - 1;
    let next = -1;
    switch (event.key) {
      case "ArrowLeft":
        if (!stack.length) return;
        event.preventDefault();
        pop();
        return;
      case "ArrowRight":
        if (current?.getAttribute("aria-haspopup") !== "menu") return;
        event.preventDefault();
        current.click();
        return;
      case "ArrowDown":
        next = index < 0 ? 0 : index < last ? index + 1 : loop ? 0 : index;
        break;
      case "ArrowUp":
        next = index < 0 ? last : index > 0 ? index - 1 : loop ? last : index;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = last;
        break;
    }
    if (next < 0 || !rows[next]) return;
    event.preventDefault();
    rows[next].focus();
  };

  // opened from the keyboard: the first row takes focus, like the desktop menu (a tap leaves it on the sheet)
  const { onOpenAutoFocus, ...dismissRest } = dismiss;
  const handleOpenAutoFocus = (event: Event) => {
    onOpenAutoFocus?.(event);
    if (event.defaultPrevented || !keyboardOpenRef.current) return;
    keyboardOpenRef.current = false;
    const row = viewportRef.current?.querySelector<HTMLElement>(ROW_SELECTOR);
    if (!row) return;
    event.preventDefault();
    row.focus({ preventScroll: true });
  };

  const hasTitle = filled(title);
  const hasDescription = filled(description);
  const label = typeof title === "string" && title.trim() ? title : sheetTitle;
  const pages: SheetPageEntry[] = [{ id: ROOT_PAGE, title: null, from: null }, ...stack];

  return (
    <DrawerContent
      size="sm"
      container={container}
      data-slot="dropdown-menu-content"
      data-sheet=""
      className={cn(classNames?.content, classNames?.sheet, className)}
      classNames={SHEET_DRAWER_CLASSNAMES}
      onKeyDown={handleKeyDown}
      // radix's documented opt-out when there is nothing to describe the sheet
      {...(hasDescription ? {} : { "aria-describedby": undefined })}
      {...dismissRest}
      onOpenAutoFocus={handleOpenAutoFocus}
    >
      <DrawerHeader align="center" title={hasTitle ? title : sheetTitle} hideTitle={!hasTitle} description={description} />
      <DrawerBody ref={bodyRef} className="px-2 pt-1 pb-2 [:is([data-slot=drawer-handle],[data-slot=drawer-title])+&]:pt-2">
        <SheetEngineContext.Provider value={engine}>
          <motion.div
            ref={viewportRef}
            data-slot="dropdown-menu-sheet-viewport"
            className="relative -mx-2 overflow-clip"
            initial={false}
            animate={{ height }}
            transition={reduced ? { duration: 0 } : springs.smooth}
          >
            <AnimatePresence initial={false}>
              {pages.map((page, index) => (
                <SheetPage
                  key={page.id}
                  id={page.id}
                  root={index === 0}
                  top={index === pages.length - 1}
                  title={page.title}
                  label={label}
                  backLabel={backLabel}
                  onBack={pop}
                  onHeight={onHeight}
                  onRegister={onRegister}
                >
                  {index === 0 ? children : null}
                </SheetPage>
              ))}
            </AnimatePresence>
          </motion.div>
        </SheetEngineContext.Provider>
      </DrawerBody>
      {showCancel ? (
        <DrawerFooter className="px-4 pt-2">
          <DrawerClose asChild>
            <Button data-slot="dropdown-menu-cancel" variant="soft" size="lg" fullWidth>
              {cancelLabel}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      ) : null}
    </DrawerContent>
  );
}

// ---------------------------------------------------------------- content

export type DropdownMenuContentProps = Omit<
  ComponentProps<typeof DropdownMenuPrimitive.Content>,
  "asChild" | "forceMount" | "title"
> & {
  // data entries, rendered before any JSX children
  items?: MenuEntry[];
  // item height 32 / 36px (44px on touch either way), text 13 / 14px
  size?: DropdownMenuSize;
  radius?: DropdownMenuRadius;
  // px
  minWidth?: number;
  // the action sheet's centered header on phones; not shown on desktop
  title?: ReactNode;
  description?: ReactNode;
  // the sheet's hidden title when there is no title
  sheetTitle?: string;
  // the sheet's Cancel button
  showCancel?: boolean;
  cancelLabel?: string;
  // the back row of a submenu page in the sheet
  backLabel?: string;
  // sheet only (the desktop menu focuses itself or its first item); preventDefault to focus something else
  onOpenAutoFocus?: (event: Event) => void;
  container?: HTMLElement | null;
  classNames?: Partial<Record<DropdownMenuSlot, string>>;
};

function DropdownMenuContentRender(dropdownMenuContentProps: DropdownMenuContentProps) {
  const {
    items,
    side = "bottom",
    align = "start",
    sideOffset = 8,
    size = "md",
    radius = "md",
    minWidth = 192,
    loop = true,
    collisionPadding = 12,
    title,
    description,
    sheetTitle = "Actions",
    showCancel = true,
    cancelLabel = "Cancel",
    backLabel = "Back",
    container,
    classNames,
    className,
    style,
    onKeyDown,
    onOpenAutoFocus,
    onCloseAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onInteractOutside,
    onFocusOutside,
    children,
    ...props
  } = useDefaults("dropdown-menu-content", dropdownMenuContentProps);
  const root = useRoot("DropdownMenuContent");
  const popover = useMotionPreset("popover");
  const { register, onKeyDown: runShortcut } = useShortcutRegistry();
  const safeSize: DropdownMenuSize = SIZES.includes(size) ? size : "md";
  const safeRadius: DropdownMenuRadius = RADII.includes(radius) ? radius : "md";
  const width = Number.isFinite(minWidth) ? minWidth : 192;
  const { keyboardOpenRef } = root;

  // opened from the keyboard: the first enabled item takes focus (radix focuses the panel itself)
  const focusFirst = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !keyboardOpenRef.current) return;
      keyboardOpenRef.current = false;
      // no cleanup: radix recomposes the ref on every render, which would cancel the frame right away
      requestAnimationFrame(() => {
        const active = document.activeElement;
        if (!node.isConnected || (active && active !== node && node.contains(active))) return;
        node.querySelector<HTMLElement>('[role^="menuitem"]:not([data-disabled])')?.focus({ preventScroll: true });
      });
    },
    [keyboardOpenRef],
  );

  const value = useMemo<ContentContextValue>(
    () => ({ size: safeSize, radius: safeRadius, minWidth: width, loop, classNames, register }),
    [safeSize, safeRadius, width, loop, classNames, register],
  );

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    onKeyDown?.(event as ReactKeyboardEvent<HTMLDivElement>);
    runShortcut(event);
  };

  const body = (
    <>
      <MenuEntries items={items} />
      {children}
    </>
  );

  if (root.sheet) {
    return (
      <ContentContext.Provider value={value}>
        <SheetSurface
          title={title}
          description={description}
          sheetTitle={sheetTitle}
          showCancel={showCancel}
          cancelLabel={cancelLabel}
          backLabel={backLabel}
          loop={loop}
          container={container === undefined ? root.container : container}
          classNames={classNames}
          className={className}
          onKeyDown={handleKeyDown}
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
          onEscapeKeyDown={onEscapeKeyDown}
          onPointerDownOutside={onPointerDownOutside}
          onInteractOutside={onInteractOutside}
          onFocusOutside={onFocusOutside}
        >
          {body}
        </SheetSurface>
      </ContentContext.Provider>
    );
  }

  return (
    <ContentContext.Provider value={value}>
      <AnimatePresence>
        {root.open && (
          <DropdownMenuPrimitive.Portal forceMount container={(container === undefined ? root.container : container) ?? undefined}>
            <DropdownMenuPrimitive.Content
              forceMount
              asChild
              side={side}
              align={align}
              sideOffset={sideOffset}
              collisionPadding={collisionPadding}
              loop={loop}
              onKeyDown={handleKeyDown}
              onCloseAutoFocus={onCloseAutoFocus}
              onEscapeKeyDown={onEscapeKeyDown}
              onPointerDownOutside={onPointerDownOutside}
              onInteractOutside={onInteractOutside}
              onFocusOutside={onFocusOutside}
              {...props}
            >
              <motion.div
                ref={focusFirst}
                {...popover}
                data-slot="dropdown-menu-content"
                data-size={safeSize}
                data-radius={safeRadius}
                className={cn(
                  MENU_CLASSES.content,
                  "max-h-[var(--radix-dropdown-menu-content-available-height)] data-[state=closed]:pointer-events-none",
                  MENU_RADII[safeRadius].content,
                  classNames?.content,
                  className,
                )}
                style={{
                  transformOrigin: "var(--radix-dropdown-menu-content-transform-origin)",
                  minWidth: width,
                  ...(style as object),
                }}
              >
                {body}
              </motion.div>
            </DropdownMenuPrimitive.Content>
          </DropdownMenuPrimitive.Portal>
        )}
      </AnimatePresence>
    </ContentContext.Provider>
  );
}

// ---------------------------------------------------------------- items

type RowProps = Record<string, unknown>;
type ClickHandler = ((event: ReactMouseEvent<HTMLElement>) => void) | undefined;

// a sheet row keeps the caller's onClick (radix composes it on desktop); a prevented click skips the row's action
function composeClick(theirs: unknown, ours: (event: ReactMouseEvent<HTMLElement>) => void) {
  return (event: ReactMouseEvent<HTMLElement>) => {
    (theirs as ClickHandler)?.(event);
    if (!event.defaultPrevented) ours(event);
  };
}

export type DropdownMenuItemProps = Omit<ComponentProps<typeof DropdownMenuPrimitive.Item>, "onSelect"> & {
  icon?: ReactNode;
  description?: ReactNode;
  // mod+k notation; shown at the end and run while the menu is open
  shortcut?: string;
  badge?: ReactNode;
  tone?: MenuTone;
  // selecting it does not close the menu
  keepOpen?: boolean;
  // renders an <a>
  href?: string;
  target?: string;
  rel?: string;
  onSelect?: (event: Event) => void;
};

function DropdownMenuItemRender({
  icon,
  description,
  shortcut,
  badge,
  tone = "neutral",
  keepOpen = false,
  href,
  target,
  rel,
  asChild = false,
  disabled = false,
  onSelect,
  textValue,
  className,
  children,
  ...props
}: DropdownMenuItemProps) {
  const { sheet } = useRoot("DropdownMenuItem");
  const content = useContext(ContentContext);
  const run = useSelectRunner();
  const { aria } = useShortcutLabel(shortcut?.trim() || null);
  useItemShortcut(shortcut, disabled, () => run(onSelect, keepOpen));

  const safeTone: MenuTone = tone === "danger" ? "danger" : "neutral";
  const child = asChild && isValidElement<{ children?: ReactNode; className?: string; onClick?: (event: ReactMouseEvent) => void }>(children)
    ? (children as ReactElement<{ children?: ReactNode; className?: string; onClick?: (event: ReactMouseEvent) => void }>)
    : null;
  const inner = (label: ReactNode) => (
    <MenuItemContent
      label={label}
      icon={icon}
      description={description}
      badge={badge}
      shortcut={shortcut}
      tone={safeTone}
      size={content.size}
      sheet={sheet}
      classNames={content.classNames}
    />
  );
  const shared = {
    "data-slot": "dropdown-menu-item",
    "data-tone": safeTone,
    "aria-keyshortcuts": aria,
  };

  if (sheet) {
    const rowClass = cn(menuSheetItemClass({ tone: safeTone, description: filled(description) }), content.classNames?.item, className);
    const rest = props as RowProps;
    if (child) {
      return cloneElement(
        child,
        {
          ...rest,
          ...shared,
          role: "menuitem",
          "data-sheet-row": "",
          "aria-disabled": disabled || undefined,
          "data-disabled": disabled ? "" : undefined,
          className: cn(rowClass, child.props.className),
          onClick: (event: ReactMouseEvent) => {
            (rest.onClick as ClickHandler)?.(event as ReactMouseEvent<HTMLElement>);
            child.props.onClick?.(event);
            if (disabled) event.preventDefault();
            else if (!event.defaultPrevented) run(onSelect, keepOpen);
          },
        } as RowProps,
        inner(child.props.children),
      );
    }
    if (href) {
      return (
        <a
          {...rest}
          {...shared}
          role="menuitem"
          data-sheet-row=""
          href={disabled ? undefined : href}
          target={target}
          rel={rel}
          aria-disabled={disabled || undefined}
          data-disabled={disabled ? "" : undefined}
          className={rowClass}
          onClick={(event) => {
            (rest.onClick as ((event: ReactMouseEvent) => void) | undefined)?.(event);
            if (disabled) event.preventDefault();
            else if (!event.defaultPrevented) run(onSelect, keepOpen);
          }}
        >
          {inner(children)}
        </a>
      );
    }
    return (
      <button
        {...rest}
        {...shared}
        type="button"
        role="menuitem"
        data-sheet-row=""
        disabled={disabled}
        className={rowClass}
        onClick={(event) => {
          (rest.onClick as ((event: ReactMouseEvent) => void) | undefined)?.(event);
          if (!event.defaultPrevented) run(onSelect, keepOpen);
        }}
      >
        {inner(children)}
      </button>
    );
  }

  return (
    <DropdownMenuPrimitive.Item
      {...shared}
      asChild={Boolean(child || href)}
      disabled={disabled}
      textValue={textValue ?? (typeof children === "string" ? children : undefined)}
      onSelect={(event) => {
        onSelect?.(event);
        if (keepOpen) event.preventDefault();
      }}
      className={cn(
        menuItemClass({ size: content.size, radius: content.radius, tone: safeTone, description: filled(description) }),
        content.classNames?.item,
        className,
      )}
      {...props}
    >
      {child ? (
        cloneElement(
          child,
          {
            // a router link prevents the click to navigate itself, which also stops radix from selecting: close here
            onClick: (event: ReactMouseEvent) => {
              child.props.onClick?.(event);
              if (event.defaultPrevented && !disabled) run(onSelect, keepOpen);
            },
          } as RowProps,
          inner(child.props.children),
        )
      ) : href ? (
        <a href={href} target={target} rel={rel}>
          {inner(children)}
        </a>
      ) : (
        inner(children)
      )}
    </DropdownMenuPrimitive.Item>
  );
}

export type DropdownMenuCheckboxItemProps = Omit<
  ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>,
  "onSelect" | "checked" | "onCheckedChange"
> & {
  checked?: boolean | "indeterminate";
  defaultChecked?: boolean | "indeterminate";
  onCheckedChange?: (checked: boolean) => void;
  icon?: ReactNode;
  description?: ReactNode;
  shortcut?: string;
  // default true: toggling keeps the menu open
  keepOpen?: boolean;
  onSelect?: (event: Event) => void;
};

function DropdownMenuCheckboxItemRender({
  checked: checkedProp,
  defaultChecked = false,
  onCheckedChange,
  icon,
  description,
  shortcut,
  keepOpen = true,
  disabled = false,
  onSelect,
  textValue,
  className,
  children,
  ...props
}: DropdownMenuCheckboxItemProps) {
  const { sheet } = useRoot("DropdownMenuCheckboxItem");
  const content = useContext(ContentContext);
  const run = useSelectRunner();
  const [checked, setChecked] = useControllableState<boolean | "indeterminate">({
    value: checkedProp,
    defaultValue: defaultChecked,
    onChange: (next) => onCheckedChange?.(next === true),
  });
  const { aria } = useShortcutLabel(shortcut?.trim() || null);
  const toggle = () => {
    setChecked((current) => current !== true);
    run(onSelect, keepOpen);
  };
  useItemShortcut(shortcut, disabled, toggle);

  const inner = (
    <MenuItemContent
      label={children}
      icon={icon}
      description={description}
      shortcut={shortcut}
      indicator="checkbox"
      checked={checked}
      size={content.size}
      sheet={sheet}
      classNames={content.classNames}
    />
  );
  const state = checked === "indeterminate" ? "indeterminate" : checked ? "checked" : "unchecked";

  if (sheet) {
    return (
      <button
        {...(props as RowProps)}
        type="button"
        role="menuitemcheckbox"
        aria-checked={checked === "indeterminate" ? "mixed" : checked}
        aria-keyshortcuts={aria}
        data-slot="dropdown-menu-checkbox-item"
        data-state={state}
        data-sheet-row=""
        disabled={disabled}
        className={cn(menuSheetItemClass({ description: filled(description) }), content.classNames?.item, className)}
        onClick={composeClick((props as RowProps).onClick, toggle)}
      >
        {inner}
      </button>
    );
  }

  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      aria-keyshortcuts={aria}
      checked={checked}
      onCheckedChange={(next) => setChecked(next)}
      disabled={disabled}
      textValue={textValue ?? (typeof children === "string" ? children : undefined)}
      onSelect={(event) => {
        onSelect?.(event);
        if (keepOpen) event.preventDefault();
      }}
      className={cn(
        menuItemClass({ size: content.size, radius: content.radius, description: filled(description) }),
        content.classNames?.item,
        className,
      )}
      {...props}
    >
      {inner}
    </DropdownMenuPrimitive.CheckboxItem>
  );
}

export type DropdownMenuRadioGroupProps = Omit<
  ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>,
  "value" | "defaultValue" | "onValueChange"
> & {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
};

function DropdownMenuRadioGroupRender({
  value: valueProp,
  defaultValue = "",
  onValueChange,
  className,
  children,
  ...props
}: DropdownMenuRadioGroupProps) {
  const { sheet } = useRoot("DropdownMenuRadioGroup");
  const [value, setValue] = useControllableState({ value: valueProp, defaultValue, onChange: onValueChange });
  const context = useMemo<RadioContextValue>(() => ({ value, setValue }), [value, setValue]);

  return (
    <RadioContext.Provider value={context}>
      {sheet ? (
        <div {...(props as RowProps)} role="group" data-slot="dropdown-menu-radio-group" className={cn("flex flex-col", className)}>
          {children}
        </div>
      ) : (
        <DropdownMenuPrimitive.RadioGroup
          data-slot="dropdown-menu-radio-group"
          value={value}
          onValueChange={setValue}
          className={className}
          {...props}
        >
          {children}
        </DropdownMenuPrimitive.RadioGroup>
      )}
    </RadioContext.Provider>
  );
}

export type DropdownMenuRadioItemProps = Omit<ComponentProps<typeof DropdownMenuPrimitive.RadioItem>, "onSelect"> & {
  icon?: ReactNode;
  description?: ReactNode;
  // default true: picking it keeps the menu open
  keepOpen?: boolean;
  onSelect?: (event: Event) => void;
};

function DropdownMenuRadioItemRender({
  value,
  icon,
  description,
  keepOpen = true,
  disabled = false,
  onSelect,
  textValue,
  className,
  children,
  ...props
}: DropdownMenuRadioItemProps) {
  const { sheet } = useRoot("DropdownMenuRadioItem");
  const content = useContext(ContentContext);
  const group = useContext(RadioContext);
  const run = useSelectRunner();
  const checked = group?.value === value;

  const inner = (
    <MenuItemContent
      label={children}
      icon={icon}
      description={description}
      indicator="radio"
      checked={checked}
      size={content.size}
      sheet={sheet}
      classNames={content.classNames}
    />
  );

  if (sheet) {
    return (
      <button
        {...(props as RowProps)}
        type="button"
        role="menuitemradio"
        aria-checked={checked}
        data-slot="dropdown-menu-radio-item"
        data-state={checked ? "checked" : "unchecked"}
        data-sheet-row=""
        disabled={disabled}
        className={cn(menuSheetItemClass({ description: filled(description) }), content.classNames?.item, className)}
        onClick={composeClick((props as RowProps).onClick, () => {
          group?.setValue(value);
          run(onSelect, keepOpen);
        })}
      >
        {inner}
      </button>
    );
  }

  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      value={value}
      disabled={disabled}
      textValue={textValue ?? (typeof children === "string" ? children : undefined)}
      onSelect={(event) => {
        onSelect?.(event);
        if (keepOpen) event.preventDefault();
      }}
      className={cn(
        menuItemClass({ size: content.size, radius: content.radius, description: filled(description) }),
        content.classNames?.item,
        className,
      )}
      {...props}
    >
      {inner}
    </DropdownMenuPrimitive.RadioItem>
  );
}

// ---------------------------------------------------------------- submenus

export type DropdownMenuSubProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
};

function DropdownMenuSubRender({ open: openProp, defaultOpen = false, onOpenChange, children }: DropdownMenuSubProps) {
  const { sheet } = useRoot("DropdownMenuSub");
  const id = useId();
  const [open, setOpen] = useControllableState({ value: openProp, defaultValue: defaultOpen, onChange: onOpenChange });
  const value = useMemo<SubContextValue>(() => ({ id, open }), [id, open]);
  return (
    <SubContext.Provider value={value}>
      {sheet ? (
        children
      ) : (
        <DropdownMenuPrimitive.Sub open={open} onOpenChange={setOpen}>
          {children}
        </DropdownMenuPrimitive.Sub>
      )}
    </SubContext.Provider>
  );
}

function useSub(part: string) {
  const context = useContext(SubContext);
  if (!context) throw new Error(`<${part}> must be used inside <DropdownMenuSub>`);
  return context;
}

export type DropdownMenuSubTriggerProps = ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  icon?: ReactNode;
  description?: ReactNode;
  badge?: ReactNode;
};

function DropdownMenuSubTriggerRender({
  icon,
  description,
  badge,
  disabled = false,
  textValue,
  className,
  children,
  ...props
}: DropdownMenuSubTriggerProps) {
  const { sheet } = useRoot("DropdownMenuSubTrigger");
  const sub = useSub("DropdownMenuSubTrigger");
  const content = useContext(ContentContext);
  const engine = useContext(SheetEngineContext);

  const inner = (
    <MenuItemContent
      label={children}
      icon={icon}
      description={description}
      badge={badge}
      submenu
      size={content.size}
      sheet={sheet}
      classNames={content.classNames}
    />
  );

  if (sheet) {
    return (
      <button
        {...(props as RowProps)}
        type="button"
        role="menuitem"
        aria-haspopup="menu"
        data-slot="dropdown-menu-sub-trigger"
        data-sheet-row=""
        disabled={disabled}
        className={cn(menuSheetItemClass({ description: filled(description) }), content.classNames?.item, className)}
        onClick={composeClick((props as RowProps).onClick, (event) =>
          engine?.push({ id: sub.id, title: children, from: event.currentTarget }),
        )}
      >
        {inner}
      </button>
    );
  }

  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      disabled={disabled}
      textValue={textValue ?? (typeof children === "string" ? children : undefined)}
      className={cn(
        menuItemClass({ size: content.size, radius: content.radius, description: filled(description) }),
        MENU_CLASSES.subTrigger,
        content.classNames?.item,
        className,
      )}
      {...props}
    >
      {inner}
    </DropdownMenuPrimitive.SubTrigger>
  );
}

export type DropdownMenuSubContentProps = Omit<
  ComponentProps<typeof DropdownMenuPrimitive.SubContent>,
  "asChild" | "forceMount"
> & {
  items?: MenuEntry[];
  container?: HTMLElement | null;
};

function DropdownMenuSubContentRender({
  items,
  sideOffset = 10,
  alignOffset = -6,
  collisionPadding = 12,
  container,
  className,
  style,
  children,
  ...props
}: DropdownMenuSubContentProps) {
  const root = useRoot("DropdownMenuSubContent");
  const sub = useSub("DropdownMenuSubContent");
  const content = useContext(ContentContext);
  const engine = useContext(SheetEngineContext);
  const popover = useMotionPreset("popover");

  const body = (
    <>
      <MenuEntries items={items} />
      {children}
    </>
  );

  // the sheet pushes a page for the submenu; its rows render into that page
  if (root.sheet) {
    const target = engine?.containers[sub.id];
    return target ? createPortal(body, target) : null;
  }

  return (
    <AnimatePresence>
      {sub.open && (
        <DropdownMenuPrimitive.Portal forceMount container={(container === undefined ? root.container : container) ?? undefined}>
          <DropdownMenuPrimitive.SubContent
            forceMount
            asChild
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            collisionPadding={collisionPadding}
            loop={content.loop}
            {...props}
          >
            <motion.div
              {...popover}
              data-slot="dropdown-menu-sub-content"
              className={cn(
                MENU_CLASSES.content,
                "max-h-[var(--radix-dropdown-menu-content-available-height)] data-[state=closed]:pointer-events-none",
                MENU_RADII[content.radius].content,
                content.classNames?.content,
                className,
              )}
              style={{
                transformOrigin: "var(--radix-dropdown-menu-content-transform-origin)",
                minWidth: content.minWidth,
                ...(style as object),
              }}
            >
              {body}
            </motion.div>
          </DropdownMenuPrimitive.SubContent>
        </DropdownMenuPrimitive.Portal>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------- group, label, separator

export type DropdownMenuGroupProps = ComponentProps<typeof DropdownMenuPrimitive.Group>;

function DropdownMenuGroupRender({ className, ...props }: DropdownMenuGroupProps) {
  const { sheet } = useRoot("DropdownMenuGroup");
  if (sheet) return <div role="group" data-slot="dropdown-menu-group" className={cn("flex flex-col", className)} {...(props as RowProps)} />;
  return <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" className={className} {...props} />;
}

export type DropdownMenuLabelProps = ComponentProps<typeof DropdownMenuPrimitive.Label>;

function DropdownMenuLabelRender({ className, ...props }: DropdownMenuLabelProps) {
  const { sheet } = useRoot("DropdownMenuLabel");
  const content = useContext(ContentContext);
  if (sheet)
    return (
      <div
        data-slot="dropdown-menu-label"
        className={cn(MENU_CLASSES.sheetGroupLabel, className)}
        {...(props as RowProps)}
      />
    );
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={cn(MENU_CLASSES.groupLabel, content.size === "sm" && "px-2", className)}
      {...props}
    />
  );
}

export type DropdownMenuSeparatorProps = ComponentProps<typeof DropdownMenuPrimitive.Separator>;

function DropdownMenuSeparatorRender({ className, ...props }: DropdownMenuSeparatorProps) {
  const { sheet } = useRoot("DropdownMenuSeparator");
  const content = useContext(ContentContext);
  if (sheet)
    return (
      <div
        role="separator"
        data-slot="dropdown-menu-separator"
        className={cn(MENU_CLASSES.sheetSeparator, content.classNames?.separator, className)}
        {...(props as RowProps)}
      />
    );
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn(MENU_CLASSES.separator, content.classNames?.separator, className)}
      {...props}
    />
  );
}

// ---------------------------------------------------------------- data entries

// groups and radio sets are sections: a separator goes between a section and whatever sits next to it
const isSection = (entry: MenuEntry) => entry.type === "group" || entry.type === "radio";

function MenuEntries({ items }: { items?: MenuEntry[] }) {
  if (!items?.length) return null;
  const nodes: ReactNode[] = [];
  items.forEach((entry, index) => {
    const previous = items[index - 1];
    if (
      previous &&
      previous.type !== "separator" &&
      entry.type !== "separator" &&
      (isSection(entry) || isSection(previous))
    ) {
      nodes.push(<DropdownMenuSeparator key={`auto-separator-${index}`} />);
    }
    nodes.push(<MenuEntryNode key={entry.id ?? `${entry.type ?? "item"}-${index}`} entry={entry} />);
  });
  return <>{nodes}</>;
}

function SubmenuEntry({ entry }: { entry: MenuSubmenuEntry }) {
  useEntryShortcuts(entry.items, Boolean(entry.disabled));
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger icon={entry.icon} description={entry.description} disabled={entry.disabled}>
        {entry.label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent items={entry.items} />
    </DropdownMenuSub>
  );
}

function MenuEntryNode({ entry }: { entry: MenuEntry }) {
  switch (entry.type) {
    case "separator":
      return <DropdownMenuSeparator />;
    case "label":
      return <DropdownMenuLabel>{entry.label}</DropdownMenuLabel>;
    case "group":
      return (
        <DropdownMenuGroup>
          {filled(entry.label) && <DropdownMenuLabel>{entry.label}</DropdownMenuLabel>}
          <MenuEntries items={entry.items} />
        </DropdownMenuGroup>
      );
    case "checkbox":
      return (
        <DropdownMenuCheckboxItem
          checked={entry.checked}
          onCheckedChange={entry.onCheckedChange}
          icon={entry.icon}
          description={entry.description}
          shortcut={entry.shortcut}
          disabled={entry.disabled}
          keepOpen={entry.keepOpen ?? true}
        >
          {entry.label}
        </DropdownMenuCheckboxItem>
      );
    case "radio":
      return (
        <DropdownMenuRadioGroup value={entry.value} onValueChange={entry.onValueChange}>
          {filled(entry.label) && <DropdownMenuLabel>{entry.label}</DropdownMenuLabel>}
          {entry.options.map((option) => (
            <DropdownMenuRadioItem
              key={option.value}
              value={option.value}
              icon={option.icon}
              description={option.description}
              disabled={option.disabled}
              keepOpen={entry.keepOpen ?? true}
            >
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      );
    case "submenu":
      return <SubmenuEntry entry={entry} />;
    default:
      return (
        <DropdownMenuItem
          icon={entry.icon}
          description={entry.description}
          shortcut={entry.shortcut}
          badge={entry.badge}
          tone={entry.tone}
          href={entry.href}
          target={entry.target}
          rel={entry.target === "_blank" ? "noreferrer" : undefined}
          disabled={entry.disabled}
          keepOpen={entry.keepOpen ?? false}
          onSelect={entry.onSelect}
        >
          {entry.label}
        </DropdownMenuItem>
      );
  }
}

// ---------------------------------------------------------------- menu sheet (context menus, split buttons)

export type MenuSheetProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  items?: MenuEntry[];
  // the centered header, e.g. the name of what the actions apply to
  title?: ReactNode;
  description?: ReactNode;
  sheetTitle?: string;
  showCancel?: boolean;
  cancelLabel?: string;
  backLabel?: string;
  loop?: boolean;
  container?: HTMLElement | null;
  classNames?: Partial<Record<DropdownMenuSlot, string>>;
  className?: string;
  // extra rows after the items, built from the DropdownMenu parts
  children?: ReactNode;
  // where focus goes on close (there is no trigger to return to)
  onCloseAutoFocus?: (event: Event) => void;
};

// the action sheet on its own, with its own drawer root: open it from anything (a long press, a split button)
function MenuSheetRender({
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  items,
  title,
  description,
  sheetTitle = "Actions",
  showCancel = true,
  cancelLabel = "Cancel",
  backLabel = "Back",
  loop = true,
  container,
  classNames,
  className,
  children,
  onCloseAutoFocus,
}: MenuSheetProps) {
  const [open, setOpen] = useControllableState({ value: openProp, defaultValue: defaultOpen, onChange: onOpenChange });
  const { register, onKeyDown } = useShortcutRegistry();
  const root = useMemo<RootContextValue>(
    () => ({ sheet: true, open, setOpen, container, shortcut: undefined, keyboardOpenRef: { current: false } }),
    [open, setOpen, container],
  );
  const content = useMemo<ContentContextValue>(
    () => ({ ...DEFAULT_CONTENT, loop, classNames, register }),
    [loop, classNames, register],
  );

  return (
    <RootContext.Provider value={root}>
      <ContentContext.Provider value={content}>
        <DrawerRoot open={open} onOpenChange={setOpen} container={container}>
          <SheetSurface
            title={title}
            description={description}
            sheetTitle={sheetTitle}
            showCancel={showCancel}
            cancelLabel={cancelLabel}
            backLabel={backLabel}
            loop={loop}
            container={container}
            classNames={classNames}
            className={className}
            onKeyDown={onKeyDown}
            onCloseAutoFocus={onCloseAutoFocus}
          >
            <MenuEntries items={items} />
            {children}
          </SheetSurface>
        </DrawerRoot>
      </ContentContext.Provider>
    </RootContext.Provider>
  );
}

// ---------------------------------------------------------------- simple api

export type DropdownMenuProps = Omit<DropdownMenuRootProps, "children" | "dir"> &
  Omit<DropdownMenuContentProps, "children"> & {
    // the element that opens it (rendered with asChild: a single element that takes a ref, like a Button)
    trigger?: ReactNode;
    // extra rows after the items, built from the parts
    children?: ReactNode;
  };

function DropdownMenuRender(dropdownMenuProps: DropdownMenuProps) {
  const {
    open,
    defaultOpen = false,
    onOpenChange,
    modal = true,
    responsive = true,
    shortcut,
    container,
    trigger,
    items,
    side = "bottom",
    align = "start",
    sideOffset = 8,
    size = "md",
    radius = "md",
    minWidth = 192,
    loop = true,
    title,
    description,
    sheetTitle = "Actions",
    showCancel = true,
    cancelLabel = "Cancel",
    backLabel = "Back",
    classNames,
    children,
    ...props
  } = useDefaults("dropdown-menu", dropdownMenuProps);
  // nothing to pick: the trigger looks disabled and opens nothing
  const empty = !hasActionable(items) && !filled(children);
  return (
    <DropdownMenuRoot
      open={empty ? false : open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      modal={modal}
      responsive={responsive}
      shortcut={empty ? undefined : shortcut}
      container={container}
    >
      {trigger ? (
        <DropdownMenuTrigger asChild disabled={empty}>
          {trigger}
        </DropdownMenuTrigger>
      ) : null}
      <DropdownMenuContent
        items={items}
        side={side}
        align={align}
        sideOffset={sideOffset}
        size={size}
        radius={radius}
        minWidth={minWidth}
        loop={loop}
        title={title}
        description={description}
        sheetTitle={sheetTitle}
        showCancel={showCancel}
        cancelLabel={cancelLabel}
        backLabel={backLabel}
        classNames={classNames}
        {...props}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const MenuItemContent = withRef(MenuItemContentRender, "MenuItemContent");
export const DropdownMenuRoot = withRef(DropdownMenuRootRender, "DropdownMenuRoot");
export const DropdownMenuTrigger = withRef(DropdownMenuTriggerRender, "DropdownMenuTrigger");
export const DropdownMenuContent = withRef(DropdownMenuContentRender, "DropdownMenuContent");
export const DropdownMenuItem = withRef(DropdownMenuItemRender, "DropdownMenuItem");
export const DropdownMenuCheckboxItem = withRef(DropdownMenuCheckboxItemRender, "DropdownMenuCheckboxItem");
export const DropdownMenuRadioGroup = withRef(DropdownMenuRadioGroupRender, "DropdownMenuRadioGroup");
export const DropdownMenuRadioItem = withRef(DropdownMenuRadioItemRender, "DropdownMenuRadioItem");
export const DropdownMenuSub = withRef(DropdownMenuSubRender, "DropdownMenuSub");
export const DropdownMenuSubTrigger = withRef(DropdownMenuSubTriggerRender, "DropdownMenuSubTrigger");
export const DropdownMenuSubContent = withRef(DropdownMenuSubContentRender, "DropdownMenuSubContent");
export const DropdownMenuGroup = withRef(DropdownMenuGroupRender, "DropdownMenuGroup");
export const DropdownMenuLabel = withRef(DropdownMenuLabelRender, "DropdownMenuLabel");
export const DropdownMenuSeparator = withRef(DropdownMenuSeparatorRender, "DropdownMenuSeparator");
export const MenuSheet = withRef(MenuSheetRender, "MenuSheet");
export const DropdownMenu = withRef(DropdownMenuRender, "DropdownMenu");
